angular.module('EdxClassroomReportModule')
  .service('edxStudentAlertsService', [
    '$interpolate',
    '$q',
    'studentApi',
    'classroomAlertsApi',
    'studentAlertsApi',
    function(
      $interpolate,
      $q,
      studentApi,
      classroomAlertsApi,
      studentAlertsApi)
  {
    var self = this;

    // See narrative.js and dbl-alert-api.js for alert message reuse
    // possibilities in later stories.

    var ALERT_CATEGORIES = [
      'LEARNING',
      'USAGE',
      'NO_ALERT'
    ];

    self.ALERT_CATEGORIES = _.zipObject(ALERT_CATEGORIES, ALERT_CATEGORIES);

    var ALERT_TYPES = [
      'NO_ALERT',
      'SESSION_TIME',
      'LESSON_STRUGGLING'
    ];

    self.ALERT_TYPES = _.zipObject(ALERT_TYPES, ALERT_TYPES);

    var ALERTS = [{
      category: self.ALERT_CATEGORIES.LEARNING,
      type: self.ALERT_TYPES.LESSON_STRUGGLING,
      ordinal: 50
    }, {
      category: self.ALERT_CATEGORIES.USAGE,
      type: self.ALERT_TYPES.SESSION_TIME,
      ordinal: 150
    }, {
      category: self.ALERT_CATEGORIES.NO_ALERT,
      type: self.ALERT_TYPES.NO_ALERT,
      ordinal: 999999
    }];

    self.ALERTS = _.indexBy(ALERTS, 'type');

    self.flattenStudentAlerts = flattenStudentAlerts;
    self.loadCurrentStudentAlertsForClassroom = loadCurrentStudentAlertsForClassroom;
    self.loadCurrentStudentAlertsForStudent = loadCurrentStudentAlertsForStudent;
    self.selectPriorityAlert = selectPriorityAlert;
    self.selectPriorityAlerts = selectPriorityAlerts;
    self.selectAlertDblTopicNames = selectAlertDblTopicNames;

    /**
     * Load alerts for all of the students in a classroom
     *
     * Result is a promise resolving to an object with the structure
     *
     * {
     *   students: [
     *     {
     *       id: <user ID of student>,
     *       firstName: <first name of student>,
     *       lastName: <last name of student>,
     *       alerts: [
     *         {
     *           type: <ALERT_TYPES>,
     *           category: <ALERT_CATEGORIES>,
     *           ordinal: <number indicating priority lower = higher priority>,
     *           ... (Other data specific to alert type)
     *         }
     *       ]
     *     }
     *   ],
     *   date: <date/time when alerts were computed>
     * }
     *
     * @param districtId ID of district to which classroom belongs
     * @param classroomId ID of classroom
     * @param ast Why do we need this?
     * @returns {Promise}
     */
    function loadCurrentStudentAlertsForClassroom(districtId, classroomId, ast) {
      return classroomAlertsApi.getClassroomAlerts(
        districtId,
        classroomId,
        ast
      ).then(function(alerts){
        return {
          students: _.map(alerts.data, _transformStudentAlerts),
          date: alerts.report_dates.to_date
        };
      });
    }

    /**
     * Load alerts for a single student
     *
     * Result is a promise resolving to an object with the structure
     *
     *   {
     *     id: <user ID of student>,
     *     firstName: <first name of student>,
     *     lastName: <last name of student>,
     *     alerts: [
     *       {
     *         type: <ALERT_TYPES>,
     *         category: <ALERT_CATEGORIES>,
     *         ordinal: <number indicating priority lower = higher priority>,
     *         ... (Other data specific to alert type)
     *       }
     *     ]
     *   }
     *
     * @param districtId ID of district to which classroom belongs
     * @param studentId ID of the student (user)
     * @param ast Why do we need this?
     * @returns {Promise}
     */
    function loadCurrentStudentAlertsForStudent(districtId, studentId, ast) {
      return studentAlertsApi.getStudentAlerts(
        districtId,
        studentId,
        ast
      ).then(function (result) {
        return _transformStudentAlerts(result.data);
      });
    }

    /**
     * Applies selectPriorityAlert() to each student in list.
     *
     * @param studentAlerts
     * @return {Array}
     */
    function selectPriorityAlerts(studentAlerts) {
      return _.map(studentAlerts, selectPriorityAlert);
    }

    /**
     * Copies student alert object including only the highest priority alert
     *
     * The object returned from this will have an alerts array with at most 1 element.  The 1 alert is the
     * alert with the lowest ordinal.
     *
     * @param student
     * @return {*}
     */
    function selectPriorityAlert(student) {
      var copy = _.clone(student);
      copy.alerts = _.reduce(copy.alerts, function(lowest, current){
        if (current.ordinal < lowest.ordinal) {
          return current;
        } else {
          return lowest;
        }
      });
      if (copy.alerts) {
        copy.alerts = [copy.alerts];
      } else {
        copy.alerts = [];
      }
      return copy;
    }

    /**
     * Flatten the structure returned by loadCurrentStudentAlertsForClassroom() or loadCurrentStudentAlertsForStudent()
     *
     * This flattens the alerts to this structure
     *
     * [
     *   {
     *     id: <user ID of student>,
     *     firstName: <first name of student>,
     *     lastName: <last name of student>,
     *     type: <ALERT_TYPES>,
     *     category: <ALERT_CATEGORIES>,
     *     ordinal: <number indicating priority lower = higher priority>,
     *     ... (other data specific to alert type)
     *   },
     *   ...
     * ]
     *
     * Note that a student may occur multiple times in the array, once for each alert for the student
     *
     * @param studentAlerts Alerts exactly as returned by loadCurrentStudentAlertsForClassroom
     * @returns {Array}
     */
    function flattenStudentAlerts(studentAlerts) {
      var students = !!studentAlerts.students ? studentAlerts.students : studentAlerts;
      if (_.isArray(students)) {
        return _.flatten(_.map(students, _flattenSingleStudent));
      } else {
        return _flattenSingleStudent(students);
      }

    }

    function _flattenSingleStudent(student) {
      return _.map(student.alerts, function(alert) {
        return _flattenSingleAlert(student, alert);
      });
    }

    /**
     * Compute the complete set of dbl topic names for which learning alerts exist
     *
     * Accepts the output from either loadCurrentStudentAlertsForClassroom or loadCurrentStudentAlertsForStudent
     *
     * @param studentAlerts List of alerts to text
     * @return {{}}
     */
    function selectAlertDblTopicNames(studentAlerts) {
      studentAlerts = studentAlerts || [];
      var topics = {};
      var students = !!studentAlerts.students ? studentAlerts.students
        : _.isArray(studentAlerts) ? studentAlerts
        : [studentAlerts];
      if (_.isArray(students)) {
        _.each(students, function(student) {
          _.each(student.alerts, function(alert){
            if (alert.category === self.ALERT_CATEGORIES.LEARNING && alert.dblStandardName) {
              topics[alert.dblStandardName] = alert.dblStandardName;
            }
          });
        });
      }
      return topics;
    }

    /**
     * Transforms single student record from REST API into something more friendly
     *
     * @param student
     * @returns {*}
     * @private
     */
    function _transformStudentAlerts(student) {
      return {
        id: student.student_dbl_id,
        firstName: student.first_name,
        lastName: student.last_name,
        alerts: _.map(student.alerts, function(alert) {
          var alertType = _determineAlertType(alert);
          var alertMeta = self.ALERTS[alertType];
          return _.assign(_convertKeysToCamelCase(alert), alertMeta);
        })
      };
    }

    function _convertKeysToCamelCase(obj) {
      return _.mapKeys(obj, function(value, key) { return _.camelCase(key); });
    }

    /**
     * Convert the alert type string returned by the REST API into the string used in JS
     *
     * @param alert
     * @returns {*}
     * @private
     */
    function _determineAlertType(alert) {
      if (alert['@type'] === 'LessonStrugglingAlert') {
        return self.ALERT_TYPES.LESSON_STRUGGLING;
      } else if (alert['@type'] === 'SessionTimeAlert') {
        return self.ALERT_TYPES.SESSION_TIME;
      } else {
        return self.ALERT_TYPES.NO_ALERT;
      }
    }

    /**
     * Flattens a single alert
     * Combines attributes of student and alert
     *
     * @param student
     * @param alert
     * @returns {*}
     * @private
     */
    function _flattenSingleAlert(student, alert) {
      var flat = _.assign({}, student, alert);
      delete flat.alerts;
      return flat;
    }

  }]
);
